/**
 * ⭐ 任务详情（原型 js/detail.js）· 右侧三列抽屉 —— 全局容器。
 *
 * 挂在 AppShell 里；地图 / 星图点节点（或星图侧栏「编辑填报」）→ openTask(id) 唤出。
 * 对齐原型 #drawer：
 *   - 右侧固定，宽度 min(980px, 92vw)，左侧渐变光条，.34s 滑入
 *   - 抽屉头 .dh：状态点 + 任务名 + 项目/主责/分类/计划 + 徽标 + 系统计算进度
 *   - 三列并排 .dbody：指标与要素 / 产出物齐套 / 审批与留痕
 *   - 底部 .dfoot：提交审批 / 申请解冻 / 操作日志导出 + 状态守卫
 *
 * 冻结感知：FROZEN / ARCHIVED 显示紫色警示；非管理员编辑入口禁用；
 * 管理员走「申请解锁 → 窗口 → 重新报审」。
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import { ApiError } from '../../api/client';
import { useUiStore } from '../../stores/ui';
import ProjectFormDialog from '../project/ProjectFormDialog';
import { useProjectDetail } from '../project/useProjects';
import DetailHeader from './DetailHeader';
import DetailMetricColumn from './DetailMetricColumn';
import DetailCompletenessColumn from './DetailCompletenessColumn';
import DetailTimelineColumn from './DetailTimelineColumn';
import DeleteTaskDialog from './DeleteTaskDialog';
import UnlockDialog from './UnlockDialog';
import SaveTemplateDialog from './SaveTemplateDialog';
import {
  useApprovalTimeline,
  useEditPolicy,
  useStartApproval,
  useSubmitTask,
  useTaskApproval,
  useTaskAudit,
  useTaskDetail,
  useWithdraw,
} from './useTaskDetail';
import './detail.css';

export default function TaskDrawer() {
  const navigate = useNavigate();
  const taskId = useUiStore((s) => s.currentTaskId);
  const drawerOpen = useUiStore((s) => s.drawerOpen);
  const closeTask = useUiStore((s) => s.closeTask);
  /* ⭐ P7.5：打开「编辑任务」抽屉（复用新建任务的 TaskFormDrawer） */
  const openTaskEdit = useUiStore((s) => s.openTaskEdit);
  const [unlockOpen, setUnlockOpen] = useState(false);
  /** ⭐ P7.6：删除任务二次确认弹窗开关 */
  const [deleteOpen, setDeleteOpen] = useState(false);
  /** ⭐ P8.1：「存为模板」弹窗开关 */
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  /** ⭐ P6.27：审批因「无合格审批人」失败时，展示可操作指引 */
  const [approvalError, setApprovalError] = useState<{ code: string; message: string } | null>(
    null,
  );
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  /** ⭐ P6.70：抽屉根节点，用于判定点击是否落在抽屉之内 */
  const drawerRef = useRef<HTMLElement>(null);

  const { task, progress, metrics, completeness, isLoading, error } = useTaskDetail(taskId ?? '');
  const projectDetail = useProjectDetail(task.data?.projectId);
  const policy = useEditPolicy(task.data);
  const approval = useTaskApproval(taskId ?? '', Boolean(taskId));
  const timeline = useApprovalTimeline(approval.data?.id);
  const audit = useTaskAudit(taskId ?? '', Boolean(taskId));
  const startApproval = useStartApproval(taskId ?? '');
  const submitTask = useSubmitTask(taskId ?? '');
  const withdraw = useWithdraw(taskId ?? '');

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeTask();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, closeTask]);

  /* ⭐ P6.70：点击抽屉外部自动关闭（与 UserMenu / UserPicker 同一模式） */
  useEffect(() => {
    if (!drawerOpen || !taskId) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      // ① 点击落在抽屉内部 → 不处理
      if (drawerRef.current?.contains(target)) return;
      // ② 子弹窗（申请解冻 / 项目编辑）的遮罩或内容 → 不算抽屉外部
      if (target.closest('.modal-overlay, .pm-modal-overlay, [role="dialog"]')) return;
      closeTask();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [drawerOpen, taskId, closeTask]);

  useEffect(() => {
    setUnlockOpen(false);
    setDeleteOpen(false);
    setSaveTemplateOpen(false);
  }, [taskId]);

  if (!taskId || !drawerOpen) return null;

  /**
   * ⭐ P8.17：第一个独立动作 —— 只提交（POST /tasks/:id/submit）。
   * 成功后任务进入 SUBMITTED，UI 重绘一次，主按钮变为「发起审批」等待人工点击。
   */
  const onSubmitTask = () => {
    setApprovalError(null);
    submitTask.mutate(undefined, {
      onError: (err) => {
        window.alert(err instanceof ApiError ? err.message : '提交失败');
      },
    });
  };

  /**
   * ⭐ P8.17：第二个独立动作 —— 只发起审批（POST /tasks/:id/approval）。
   * 不再隐式先调用 /submit；前提（已是 SUBMITTED）由状态与按钮切换保证。
   */
  const onStartApproval = () => {
    setApprovalError(null);
    startApproval.mutate(
      {},
      {
        onError: (err) => {
          // ⭐ P6.27：审批「无合格审批人」是最常见的配置类失败，单靠 alert 用户无从下手。
          //    改为内联可操作指引（附带「去设置项目负责人」入口）。
          if (
            err instanceof ApiError &&
            (err.code === 'APPROVAL_NO_ELIGIBLE_APPROVER' ||
              err.code === 'APPROVAL_FLOW_NO_QUALIFIED_APPROVER')
          ) {
            setApprovalError({ code: err.code, message: err.message });
            return;
          }
          window.alert(err instanceof ApiError ? err.message : '发起审批失败');
        },
      },
    );
  };

  const onWithdraw = () => {
    if (
      !window.confirm(
        '撤回提交后任务将退回「草稿」，可继续修改后重新提交。确定撤回？',
      )
    ) {
      return;
    }
    withdraw.mutate(undefined, {
      onError: (err) => {
        window.alert(err instanceof ApiError ? err.message : '撤回提交失败');
      },
    });
  };

  /**
   * ⭐ P7.7：底部操作区的状态守卫文案。
   * ⚠️ 仅在【锁定 / 冻结】状态显示（作为重要提示），「可编辑」这个纯粹的常态
   *    文案不再显示 —— 用户明确要求用「编辑 / 删除」按钮替换掉那三个字，
   *    常态下由按钮本身表达可操作性，避免复古的冗余文字占据操作位。
   */
  const guard = policy.isFrozen
    ? '🔒 已冻结：非管理员不可修改（四层防线拦截）'
    : policy.state === 'SUBMITTED'
      ? '↑ 已提交，等待发起审批；如需修改可撤回提交'
      : policy.state === 'APPROVING'
        ? '⏳ 审批中已锁定编辑'
        : '';
  const showGuard = guard !== '';

  return (
    <>
      <aside id="drawer" ref={drawerRef} className="drawer on" role="dialog" aria-label="任务详情">
        {isLoading && !task.data ? (
          <div className="drawer__center">
            <Spinner label="加载任务详情…" />
          </div>
        ) : error || !task.data ? (
          <div className="drawer__center">
            <div className="drawer__err-title">任务加载失败</div>
            <div className="drawer__err-msg">{error?.message ?? '未找到任务'}</div>
            <Button variant="secondary" size="sm" onClick={closeTask}>
              关闭
            </Button>
          </div>
        ) : (
          <>
            <DetailHeader
              task={task.data}
              progress={progress.data}
              approval={approval.data}
              policy={policy}
              onClose={closeTask}
            />

            <div className="dbody">
              <DetailMetricColumn
                taskId={taskId}
                metrics={metrics.data ?? []}
                progress={progress.data}
                policy={policy}
              />
              <DetailCompletenessColumn
                completeness={completeness.data}
                taskState={task.data.state}
              />
              <DetailTimelineColumn
                task={task.data}
                progress={progress.data}
                completeness={completeness.data}
                approval={approval.data}
                timeline={timeline.data}
                logs={audit.data}
              />
            </div>

            {approvalError && (
              <div className="dapproval-err" data-testid="approval-error" role="alert">
                <div className="dapproval-err__title">⚠ 审批无法发起</div>
                <div className="dapproval-err__msg">{approvalError.message}</div>
                {approvalError.code === 'APPROVAL_NO_ELIGIBLE_APPROVER' && (
                  <div className="dapproval-err__hint">
                    {projectDetail.data && !projectDetail.data.ownerId
                      ? '本项目尚未设置负责人。请先指定项目负责人（须为启用用户），再重新发起审批。'
                      : '请确认项目负责人是启用用户；若仍失败，请到审批设置调整第 1 级的审批人（角色成员 / 组织层级）。'}
                  </div>
                )}
                <div className="dapproval-err__act">
                  {approvalError.code === 'APPROVAL_NO_ELIGIBLE_APPROVER' &&
                    task.data?.projectId && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setOwnerDialogOpen(true)}
                      >
                        去设置项目负责人
                      </Button>
                    )}
                  <Button variant="ghost" size="sm" onClick={() => setApprovalError(null)}>
                    关闭提示
                  </Button>
                </div>
              </div>
            )}

            <footer className="dfoot">
              {policy.canWithdraw && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={withdraw.isPending}
                  onClick={onWithdraw}
                >
                  ↩ 撤回提交
                </Button>
              )}
              {policy.canSubmitTask && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={submitTask.isPending}
                  onClick={onSubmitTask}
                  data-testid="task-submit"
                >
                  提交
                </Button>
              )}
              {policy.canStartApproval && (
                <Button
                  variant="primary"
                  size="sm"
                  disabled={startApproval.isPending}
                  onClick={onStartApproval}
                  data-testid="task-start-approval"
                >
                  发起审批
                </Button>
              )}
              {policy.canApprove && (
                <Button variant="primary" size="sm" onClick={() => navigate('/approvals')}>
                  审批通过
                </Button>
              )}
              {policy.canRequestUnlock && (
                <Button variant="danger" size="sm" onClick={() => setUnlockOpen(true)}>
                  🔓 申请解冻（管理员）
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => window.print()}>
                完整操作日志导出
              </Button>

              {/* ⭐ P8.1：把当前任务的结构存为模板（个人 / 公共）。
                  不动既有按钮；状态无关（模板只取结构，任意状态均可存）。 */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSaveTemplateOpen(true)}
                data-testid="task-save-template"
                aria-label="存为模板"
                title="把当前任务的指标与要素结构存为模板"
              >
                ⧉ 存为模板
              </Button>

              {/* ⭐ P7.7：右侧操作区 —— guard 小字提示（仅锁定/冻结时）+ 编辑 / 删除。
                  靠右对齐（.dfoot__actions margin-left:auto）；按钮不可用时【不渲染】，
                  与既有风格一致。删除用危险色，与编辑明显区分。 */}
              <div className="dfoot__actions">
                {showGuard && <span className="dfoot__guard">{guard}</span>}
                {policy.canEditTask && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openTaskEdit(taskId)}
                    data-testid="task-edit"
                    aria-label="编辑任务"
                    title="编辑任务基本信息与指标要素"
                  >
                    ✎ 编辑
                  </Button>
                )}
                {policy.canDeleteTask && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setDeleteOpen(true)}
                    data-testid="task-delete"
                    aria-label="删除任务"
                    title="删除任务（不可恢复）"
                  >
                    删除
                  </Button>
                )}
              </div>
            </footer>
          </>
        )}
      </aside>

      <UnlockDialog
        taskId={taskId}
        taskName={task.data?.name ?? ''}
        open={unlockOpen}
        onClose={() => setUnlockOpen(false)}
      />

      {/* ⭐ P7.6：删除任务二次确认；成功后关闭弹窗与详情抽屉（缓存由 useDeleteTask 失效刷新） */}
      <DeleteTaskDialog
        task={task.data ?? null}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => {
          setDeleteOpen(false);
          closeTask();
        }}
      />

      {/* ⭐ P8.1：存为模板 */}
      <SaveTemplateDialog
        taskId={taskId}
        taskName={task.data?.name ?? ''}
        open={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
      />

      {/* ⭐ P6.27：从审批失败指引直接打开项目编辑，补填负责人 */}
      <ProjectFormDialog
        open={ownerDialogOpen}
        mode="edit"
        projectId={task.data?.projectId}
        onClose={() => setOwnerDialogOpen(false)}
        onSaved={() => {
          setOwnerDialogOpen(false);
          setApprovalError(null);
          void projectDetail.refetch();
        }}
      />
    </>
  );
}
